import { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import AdminLoader from "../Atoms/AdminLoader";
import { Bounce, toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";
import { DataContext } from "../../context/DataContext";
import { UserContext } from "../../context/user";
import { getFeedbackEnv } from "../../util/feedbackEnv";

const BUG_TYPE = "Bug Report / Site Performance Issue";

export default function BugReportForm({
  compact = false,
  /** Include email/userId for public `/basic/contact` only */
  includeClientIdentityFields = true,
  submitFeedback,
  onSubmitted,
}) {
  const { theme } = useTheme();
  const { reportModalDefaults } = useContext(DataContext);

  const { user } = useContext(UserContext);
  const formRootRef = useRef(null);

  const defaults = reportModalDefaults || {};

  const [formData, setFormData] = useState(() => ({
    type: compact ? BUG_TYPE : (defaults.type ?? BUG_TYPE),
    subject: defaults.subject ?? "",
    message: "",
  }));
  const [loading, setLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [envInfo] = useState(() => getFeedbackEnv(theme));
  const [envExpanded, setEnvExpanded] = useState(false);

  const effectiveType = compact ? BUG_TYPE : formData.type;
  const isBugReport = effectiveType === BUG_TYPE;

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const onDrop = useCallback((accepted) => {
    if (accepted.length === 0) return;
    const file = accepted[0];
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    noClick: false,
  });

  const removeScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            setScreenshot(file);
            setScreenshotPreview(URL.createObjectURL(file));
          }
          break;
        }
      }
    };
    const el = formRootRef.current;
    if (el) el.addEventListener("paste", handlePaste);
    return () => {
      if (el) el.removeEventListener("paste", handlePaste);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  const toastOptions = {
    position: "top-center",
    autoClose: 2500,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: false,
    draggable: false,
    theme,
    transition: Bounce,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("type", effectiveType);
      fd.append("subject", formData.subject);
      fd.append("message", formData.message);
      if (includeClientIdentityFields) {
        fd.append("email", user?.email ?? "");
        fd.append("userId", user?.id ?? "");
      }
      if (isBugReport || screenshot) {
        fd.append("url", envInfo.url);
        fd.append("userAgent", envInfo.userAgent);
        fd.append("viewport", envInfo.viewport);
        fd.append("screenResolution", envInfo.screenResolution);
        fd.append("theme", envInfo.theme);
        fd.append("brand", envInfo.brand);
      }
      if (screenshot) fd.append("screenshot", screenshot);

      const res = await submitFeedback(fd);
      if (res?.data) {
        toast.success(res.data.msg || "Feedback sent successfully!", toastOptions);
        setFormData((prev) => ({ ...prev, message: "" }));
        removeScreenshot();
        if (onSubmitted) onSubmitted();
      }
    } catch (err) {
      const msg = err?.response?.data?.msg || "Failed to send feedback.";
      toast.error(msg, toastOptions);
    } finally {
      setLoading(false);
    }
  };

  const intro = compact ? (
    <p className="mb-3 text-xs text-gray-500 dark:text-gray-400 leading-snug">
      Describe the issue. Optional screenshot (paste or attach).
    </p>
  ) : null;

  return (
    <div ref={formRootRef} tabIndex={-1} className="text-left">
      {intro}
      <form className={compact ? "space-y-3" : "space-y-4"} onSubmit={handleSubmit}>
        {!compact && (
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Feedback Type
            </label>
            <select
              id="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
            >
              <option value={BUG_TYPE}>Bug Report / Site Performance Issue</option>
              <option value="Feature Request">Feature Request</option>
              <option value="General Feedback">General Feedback</option>
              <option value="Downgrade Request">Downgrade Request</option>
            </select>
          </div>
        )}

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Brief description"
            className={`w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 text-sm ${compact ? "py-1.5" : "py-2"}`}
            required
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Message
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            placeholder="Tell us more..."
            className={`w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 text-sm resize-none ${compact ? "py-1.5" : "py-2"}`}
            required
          />
        </div>

        {(compact || isBugReport) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Screenshot <span className="font-normal text-gray-400">(optional)</span>
            </label>
            {!screenshot ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${compact ? "p-3" : "p-4"} ${
                  isDragActive
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                }`}
              >
                <input {...getInputProps()} />
                {!compact && (
                  <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Paste <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{navigator.platform?.includes("Mac") ? "Cmd" : "Ctrl"}+V</kbd>, drag, or browse
                </p>
                <p className="text-xs text-gray-400 mt-1">Max 5 MB</p>
              </div>
            ) : (
              <div className="relative inline-block">
                <img
                  src={screenshotPreview}
                  alt="Screenshot preview"
                  className="max-h-28 rounded-lg border border-gray-200 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white text-xs hover:bg-red-600 shadow"
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        )}

        {(compact || isBugReport) && (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setEnvExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-medium">Environment <span className="font-normal text-gray-400">(auto)</span></span>
              <svg className={`h-4 w-4 transition-transform ${envExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {envExpanded && (
              <div className="px-3 pb-2 text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5 font-mono leading-relaxed">
                <div><span className="text-gray-400 dark:text-gray-500">URL:</span> {envInfo.url}</div>
                <div><span className="text-gray-400 dark:text-gray-500">Browser:</span> {envInfo.userAgent}</div>
                <div><span className="text-gray-400 dark:text-gray-500">Viewport:</span> {envInfo.viewport}</div>
                <div><span className="text-gray-400 dark:text-gray-500">Screen:</span> {envInfo.screenResolution}</div>
                <div><span className="text-gray-400 dark:text-gray-500">Theme:</span> {envInfo.theme}</div>
                <div><span className="text-gray-400 dark:text-gray-500">Brand:</span> {envInfo.brand}</div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <AdminLoader />
        ) : (
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
            >
              Send Feedback
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
